import { Link } from 'react-router-dom';

interface BreadcrumbItem {
  label: string;
  to?: string;
}

interface BusinessHeroProps {
  name: string;
  tagline: string;
  categoryLabel: string;
  image: string;
  logo: string;
  rating: number;
  reviews: number;
  priceRange: string;
  verified: boolean;
  featured: boolean;
  offer: string;
  breadcrumbs: BreadcrumbItem[];
}

export default function BusinessHero({
  name,
  tagline,
  categoryLabel,
  image,
  logo,
  rating,
  reviews,
  priceRange,
  verified,
  featured,
  offer,
  breadcrumbs,
}: BusinessHeroProps) {
  return (
    <>
      {/* Breadcrumb */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 py-3">
          <nav className="flex items-center gap-2 text-sm" aria-label="Breadcrumb">
            {breadcrumbs.map((item, idx) => (
              <span key={idx} className="flex items-center gap-2">
                {idx > 0 && <i className="ri-arrow-right-s-line text-gray-400 text-xs"></i>}
                {item.to ? (
                  <Link
                    to={item.to}
                    className="text-gray-500 hover:text-teal-600 transition-colors cursor-pointer"
                  >
                    {item.label}
                  </Link>
                ) : (
                  <span className="text-gray-900 font-medium">{item.label}</span>
                )}
              </span>
            ))}
          </nav>
        </div>
      </div>

      {/* Hero Image */}
      <section className="relative h-[340px] lg:h-[400px] overflow-hidden">
        <img
          src={image}
          alt={`${name} - Roseville California local business`}
          title={`${name} Roseville CA`}
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>

        {/* Status badges */}
        <div className="absolute top-5 left-6 flex items-center gap-2">
          {featured && (
            <span className="px-3 py-1.5 bg-amber-500 text-white text-xs font-bold rounded-full flex items-center gap-1.5 whitespace-nowrap">
              <i className="ri-star-fill text-xs"></i> Featured
            </span>
          )}
          {verified && (
            <span className="px-3 py-1.5 bg-teal-600 text-white text-xs font-bold rounded-full flex items-center gap-1.5 whitespace-nowrap">
              <i className="ri-verified-badge-fill text-xs"></i> Verified
            </span>
          )}
        </div>

        {/* Business identity */}
        <div className="absolute bottom-0 left-0 right-0 px-6 pb-8">
          <div className="max-w-7xl mx-auto">
            {offer && (
              <span className="inline-block px-3 py-1.5 bg-amber-400 text-amber-900 text-xs font-bold rounded-lg mb-3 whitespace-nowrap">
                <i className="ri-coupon-3-line mr-1"></i> {offer}
              </span>
            )}
            <div className="flex items-end gap-4">
              <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center shadow-lg flex-shrink-0">
                <i className={`${logo} text-teal-600 text-2xl`}></i>
              </div>
              <div className="flex-1 min-w-0">
                <h1 className="text-3xl lg:text-4xl font-bold text-white mb-1">{name}</h1>
                <p className="text-white/80 text-sm italic mb-2">{tagline}</p>
                <div className="flex flex-wrap items-center gap-3">
                  <span className="px-2.5 py-1 bg-white/15 backdrop-blur-sm text-white text-xs font-medium rounded-full whitespace-nowrap">
                    {categoryLabel}
                  </span>
                  <div className="flex items-center gap-1">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <i
                        key={i}
                        className={`text-sm ${
                          i < Math.floor(rating)
                            ? 'ri-star-fill text-amber-400'
                            : i < rating
                            ? 'ri-star-half-fill text-amber-400'
                            : 'ri-star-line text-white/40'
                        }`}
                      ></i>
                    ))}
                    <span className="text-white font-bold text-sm ml-1">{rating}</span>
                    <span className="text-white/60 text-xs">({reviews} reviews)</span>
                  </div>
                  <span className="text-white/80 text-sm font-medium">{priceRange}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
